import { AnalysisType, getSegmentationAnalysisType, PickPoints, Segmentation, SegmentationControllerType, SegmentationProps } from "config/analysisConfig";
import {
  getBranchesVolumeFromSeriesId,
  getLawtVolumeFromSeriesId,
  getViewportIds,
  getVolumeFromSeriesId,
} from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";

import {
  BufferPool,
  SurfaceContext,
  BufferPoolItem,
  territoryInfo,
  mappingBranchInfo,
  Actors,
  CPRBufferKeys,
  updateCameraForAnalysis,
  updataAorticNode,
  getTaviCenterlinePolyData,
  getTaviCenterTangentAtIndex,
  captureCanvas,
  updateResetCamera,
} from "@features/analysis/rendering/Panels/Renderers/vtkManagers";
import { scheduleRender } from "@features/analysis/rendering/Panels/renderState";
import { getPalette, hexToNormalizedRGB } from "config/colorUtils";
import { configStore } from "config/configStore";
import vtkDataArray from "@kitware/vtk.js/Common/Core/DataArray";
import vtkImageData from "@kitware/vtk.js/Common/DataModel/ImageData";

import vtkPolyData from "@kitware/vtk.js/Common/DataModel/PolyData";
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import { Vector3 } from "@kitware/vtk.js/types";
import { createCircleSource } from "./vtkMarker";
import vtkColorTransferFunction from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction";
import vtkMapper from "@kitware/vtk.js/Rendering/Core/Mapper";
import vtkCamera from "@kitware/vtk.js/Rendering/Core/Camera";
import { clearAxesLinesAt } from "./axesLines";
import { CalciumAnalysisResult } from "@models/Parameter";
import { findNearBranch } from "../../utils/FunctionUtilites";
import vtkPlane from "@kitware/vtk.js/Common/DataModel/Plane";
import vtkPoints from "@kitware/vtk.js/Common/Core/Points";
import vtkCellArray from "@kitware/vtk.js/Common/Core/CellArray";
import { FFR_ARTERY_TYPE, FFR_OPACITY, FFRNames, nodeNames } from 'config/render3DConfig';
import { LightingOption } from "config/config";
import vtkProperty from "@kitware/vtk.js/Rendering/Core/Property";

function setMappingScalarColor(
  analysisType: AnalysisType,
  actor:vtkActor,
  key: string,
  normColor?: Vector3
) {
  const { colors } = configStore.get();
  const DARK_RANGE = 0.5;
  const cfunc = vtkColorTransferFunction.newInstance();
  cfunc.removeAllPoints();

  const addBaseGradient = () => {
    if(!normColor) return;
    cfunc.addRGBPoint(
      0,
      normColor[0] * DARK_RANGE,
      normColor[1] * DARK_RANGE,
      normColor[2] * DARK_RANGE,
    );
    cfunc.addRGBPoint(100, ...normColor);
    cfunc.setMappingRange(0, 100);
  };

  const addPalette = (
    palette: number[][],
    mapFn: (index: number, len: number) => number,
  ) => {
    const len = palette.length;
    palette.forEach((c, i) => {
      cfunc.addRGBPoint(mapFn(i, len), c[0], c[1], c[2]);
    });
  };

  const strategies = {
    FFR: () => {
      const palette = getPalette('paletteFFR', colors);

      const min = 0.5;
      const max = 1;

      addPalette(palette, (i, len) => min + (i / (len - 1)) * (max - min));
      cfunc.setMappingRange(min, max);
      actor.getMapper()?.setUseLookupTableScalarRange(true);
      actor.getMapper()?.setInterpolateScalarsBeforeMapping(true);
      actor.getMapper()?.setScalarVisibility(true);
      actor.getMapper()?.setScalarRange(min, max);
      actor.getMapper()?.setLookupTable(cfunc);
    },
    coronary: () => {
      if (analysisType === 'myocardial') {
        const c = hexToNormalizedRGB(colors.myocardoal_coronary);
        cfunc.addRGBPoint(50, ...c);
      } else if (analysisType === 'stenosis') {
        const palette = getPalette('autoLesion', colors);
        addPalette(palette, (i) => Math.pow(2, i));
      }
      addBaseGradient();
      cfunc.setMappingRange(0, 100);
      actor.getMapper()?.setScalarRange(0, 100);
      actor.getMapper()?.setLookupTable(cfunc);
    },

    laWall: () => {
      if (analysisType === 'wallthickness') {
        const palette = getPalette('paletteLAWT', colors);
        const min = Math.round((0 / 255) * 255 - 128); // 0
        const max = Math.round((110 / 255) * 255 - 128); //5.5 , 110 ,5.5*0.05 // 균등 간격으로 색상 추가

        addPalette(palette, (i, len) => min + (i / (len - 1)) * (max - min));

        actor.getMapper()?.setUseLookupTableScalarRange(true);
        actor.getMapper()?.setInterpolateScalarsBeforeMapping(true);
        actor.getMapper()?.setScalarVisibility(false);
        actor.getMapper()?.setLookupTable(cfunc);
      }
    },

    lvWall: () => {
      if (analysisType === 'myocardial') {
        const palette = getPalette('territory', colors);
        addPalette(palette, (i) => i + 1);
        addBaseGradient();

        cfunc.setMappingRange(0, 100);
        actor.getMapper()?.setScalarRange(0, 100);
        actor.getMapper()?.setLookupTable(cfunc);
      }
    },
  };

  strategies[key as keyof typeof strategies]?.();
  return cfunc;
}

export async function segmentation3DReader(
  context: React.MutableRefObject<SurfaceContext | null>,
  selectedSeriesId:string,
  // refSegmentation: React.RefObject<Segmentation>,
  prevDataRef: React.MutableRefObject<Record<string, any>>,
  pool: BufferPoolItem,
  analysisType: AnalysisType,
  key: string,
  segmentationProps: SegmentationProps,
  // polyData: vtkPolyData,
) {
  if (!context.current) return;
  const { actorIds, renderer, viewportId } = context.current;
  const segKey = `segmentationResult_${key}` as CPRBufferKeys;
  const data = pool[segKey]?.data;
  if (!data) return;
  if (data === prevDataRef.current[key]) return;
  if (analysisType === 'FFR') return;

  const { color, visibility, opacity } = segmentationProps;
  const normalizeColor = ([r, g, b]: Vector3): Vector3 => [
    r / 255,
    g / 255,
    b / 255,
  ];

  const normColor: Vector3 = normalizeColor(color);

  const mapper = vtkMapper.newInstance({ scalarVisibility: false });
  mapper.setInputData(data);

  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.setPickable(true);
  actor.setVisibility(visibility);
  actor.setScale(-1, 1, 1);

  const prop = actor.getProperty();
  prop.setColor(...normColor);
  prop.setInterpolationToPhong();
  prop.setOpacity(opacity);
  const lightingOptions = configStore.get().lightingOptions.Surface3D;

  setLighting(prop, lightingOptions)

  // if(analysisType === 'FFR'){
  //   if(key === 'aorta'){
  //     prop.setOpacity(FFR_OPACITY.DEFAULT);
  //   }else{
  //     actor.setVisibility(false);
  //   }
  // }
  if (['laWall', 'coronary', 'lvWall'].includes(key)) {
    setMappingScalarColor(analysisType, actor, key, normColor);
  }
  if (Object.keys(actorIds).some((k) => k === key)) {
    console.log('삭제?');
    renderer.removeActor(actorIds[key]);
  }
  renderer.addActor(actor);


  actorIds[key] = actor;
  context.current?.genericRenderWindow?.resize();
  scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export async function segmentationFFRReader(
  context: React.MutableRefObject<SurfaceContext | null>,
  selectedSeriesId:string,
  analysisType: AnalysisType,
  pool: BufferPoolItem,
  segmentation:Segmentation,
) {
  if (!context.current) return;
  const { actorIds, renderer, viewportId } = context.current;

  const normalizeColor = ([r, g, b]: Vector3): Vector3 => [
    r / 255,
    g / 255,
    b / 255,
  ];

  Object.values(FFRNames).forEach((key) => {
    const data = pool[key]?.data as vtkPolyData;
    if (!data) return;
    if(key === FFRNames.centerline) return;
    const mapper = vtkMapper.newInstance({ scalarVisibility: false });
    mapper.setInputData(data);
    if (actorIds[key]) {
      renderer.removeActor(actorIds[key]);
    }

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.setPickable(true);
    actor.getProperty().setInterpolationToPhong();
    if (key === FFRNames.cap_lca || key === FFRNames.cap_rca) {
      actor.getProperty().setOpacity(FFR_OPACITY.cap);
    } else {
      const scalarArray = data.getPointData().getArrayByName('FFR_Smoothing');
      const ffrs = scalarArray?.getData() as Float32Array;
      if (scalarArray && ffrs?.length) {
        data.getPointData().setScalars(scalarArray);
        mapper.setScalarModeToUsePointData();
        mapper.setColorModeToMapScalars();
        setMappingScalarColor(analysisType, actor, 'FFR');
      }
    }

    if(key === 'FFR_aorta'){
      const { color, visibility, opacity } = segmentation.aorta;
      const normColor: Vector3 = normalizeColor(color);
      const prop = actor.getProperty();
      prop.setColor(...normColor);
      prop.setInterpolationToPhong();
      prop.setOpacity(FFR_OPACITY.DEFAULT);
    }

    renderer.addActor(actor);
    actorIds[key] = actor;
  });

  cutterFFRActor(context, pool, selectedSeriesId, true, 'FFR_aorta');

  context.current?.genericRenderWindow?.resize();
  scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export async function updateSurfaceActorProps(
  context: React.RefObject<SurfaceContext | null>,
  key: SegmentationControllerType,
  props: Partial<SegmentationProps>,
) {
  if (!context.current) return;
  const { actorIds, viewportId, renderWindow } = context.current;
  const actor = actorIds[key] as vtkActor;
  if (!actor) {
    scheduleRender(viewportId, { type: 'updateRenderWindow' });
    return;
  }

  const color = props.color as Vector3;
  const normColor = [color[0] / 255, color[1] / 255, color[2] / 255] as Vector3;

  Object.entries(props).forEach(([propKey, propValue]) => {
    switch (propKey as keyof SegmentationProps) {
      case 'color':
        actor.getProperty().setColor(...normColor);
        break;
      case 'opacity':
        actor.getProperty().setOpacity(propValue as number);
        break;
      case 'visibility':
        actor.setVisibility(propValue as boolean);
        break;
    }
  });

  const mapper = actor.getMapper?.();
  let colorTF = mapper?.getLookupTable();
  if (colorTF) {

    updateBackgroundColorTF(key, colorTF, normColor);
    actor.getMapper()?.setLookupTable(colorTF);
  }

  actorIds[key] = actor;
  actor.modified();
  scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export function initCameraForAnalysis(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
  pool: BufferPoolItem,
) {
  if (!context.current) return;
  init3DCameraForAnalysis(
    context as React.MutableRefObject<SurfaceContext>,
    analysisType,
    seriesId,
    pool,
  );
  context.current.axes = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, 1],
  };
}


function mappingScalaIdRenderer(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool:BufferPoolItem,
  mappingName:'laWall'|'lvWall'|'coronary',
  mappingData:Record<number, number>,
  updata3d: boolean = true,
){
  if (!context.current) return;
  const { actorIds, viewportId } = context.current as SurfaceContext;
  const polyData = pool[`segmentationResult_${mappingName}` as CPRBufferKeys].data as vtkPolyData;
  const actor = actorIds[mappingName] as vtkActor;
  if (!polyData || !actor) return;
  console.log('scalaId mapping 한다',mappingName, pool)

  const points = polyData.getPoints().getData();
  const pointCount = points.length / 3;
  let interpolatedScalarColors = getScalarData(polyData,pointCount);

  for (const key in mappingData) {
    const index = Number(key);
    if(index >= pointCount) {
      console.log('나 큼')
      continue;
    }
    const scalar = mappingData[index];
    if(mappingName === 'lvWall'){
      interpolatedScalarColors[index] = scalar;
    }
    else if(mappingName === 'laWall'){
      interpolatedScalarColors[index] = Math.round((scalar / 255) * 255 - 128);
    }
  }
  polyData.getPointData().getScalars().modified();
  polyData.modified();

  actor.getMapper()?.setScalarVisibility(true);
  actor.modified();
  if(updata3d)
    scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export async function mappingRenderer(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  mappingName: 'laWall' | 'lvWall' | 'coronary',
  branchIds: number[] = [],
  updata3d: boolean = true,
  half?: boolean,
) {
  if (!context.current) return;
  const mappingData =
    mappingName === 'lvWall'
      ? (pool?.territory?.data as territoryInfo)?.territoryMappingData
      : mappingName === 'laWall'
        ? pool?.LAWT.data
        : undefined;

  if (mappingData) {
    mappingScalaIdRenderer(
      context,
      pool,
      mappingName,
      mappingData as Record<number, number>,
      updata3d,
    );
    return;
  }

  const { actorIds, viewportId } = context.current as SurfaceContext;
  let polyData: vtkPolyData;
  const renderingQuality = configStore.get().generalSetting.renderingQuality;
  const volume = getVolumeFromSeriesId(
    seriesId,
    half ?? renderingQuality !== 'high',
  );
  if (!volume) return;
  const { dimensions, imageData } = volume;
  console.log('그냥 mapping 한다', mappingName, pool);

  const mappingBranchData =
    mappingName === 'coronary'
      ? analysisType === 'myocardial'
        ? (pool?.territory?.data as territoryInfo)?.mappingBranches
        : (pool?.mapping_branches?.data as mappingBranchInfo)?.[
            analysisType === 'stenosis' ? 'stenosisBranches' : 'branches'
          ]
      : undefined;

  if (!mappingBranchData) return;

  let data: Uint8Array | undefined;
  if (mappingName === 'lvWall') {
    data = (pool?.territory?.data as territoryInfo)?.territory;
  } else if (mappingName === 'laWall') {
    data = getLawtVolumeFromSeriesId(
      seriesId,
    )?.voxelManager?.getCompleteScalarDataArray?.() as Uint8Array | undefined;
  } else {
    data =
      analysisType === 'myocardial'
        ? (pool?.['territory']?.data as territoryInfo)?.branches
        : (getBranchesVolumeFromSeriesId(seriesId)?.voxelManager
            ?.getCompleteScalarDataArray!() as Uint8Array);
  }
  if (!data) return;

  polyData = pool[`segmentationResult_${mappingName}` as CPRBufferKeys]
    .data as vtkPolyData;

  const actor = actorIds[mappingName] as vtkActor;
  if (!polyData || !actor) return;

  function mergeKeys(
    map: Record<number, number | Set<number>>,
    keys: number[],
  ): Set<number> {
    const merged = new Set<number>();
    keys.forEach((k) => {
      const v = map[k];
      if (v == null) return;

      if (v instanceof Set) {
        v.forEach((val) => merged.add(val));
      } else {
        merged.add(v);
      }
    });
    return merged;
  }
  const result = mergeKeys(
    mappingBranchData as Record<number, number | Set<number>>,
    branchIds,
  );

  const points = polyData.getPoints().getData();
  const pointCount = points.length / 3;
  let interpolatedScalarColors = getScalarData(polyData, pointCount);
  const [nx, ny, nz] = dimensions;
  for (let i = 0; i < pointCount; i++) {
    const point: Vector3 = [
      points[i * 3 + 0],
      points[i * 3 + 1],
      points[i * 3 + 2],
    ];
    const modelCoord = imageData!.worldToIndexVec3(point);
    const [x, y, z] = modelCoord.map(Math.round);
    if (x < 0 || x >= nx || y < 0 || y >= ny || z < 0 || z >= nz) {
      interpolatedScalarColors[i] = 0;
      continue;
    }
    const offset = z * ny * nx + y * nx + x;
    const scalar = data[offset];
    if (mappingName === 'laWall') {
      interpolatedScalarColors[i] = Math.round((scalar / 255) * 255 - 128);
    } else if (mappingName === 'lvWall') {
      interpolatedScalarColors[i] = scalar;
    } else if (mappingName === 'coronary') {
      if (analysisType === 'stenosis' || analysisType === 'myocardial') {
        const val = (mappingBranchData as Record<number, number>)[scalar];
        if (analysisType === 'myocardial') {
          //왜 50이냐면 colors.myocardoal_coronary 색상 추가했긴때문이지.
          interpolatedScalarColors[i] = val === 1 ? 50 : 100;
        } else {
          interpolatedScalarColors[i] =
            val === 0 ? 100 : [1, 2, 4, 8].includes(val) ? val : 100;
        }
      } else {
        interpolatedScalarColors[i] = result.has(scalar) ? 100 : 0;
      }
    }
  }
  polyData.getPointData().getScalars().modified();
  polyData.modified();

  actor.getMapper()?.setScalarVisibility(true);
  actor.modified();
  if (updata3d) scheduleRender(viewportId, { type: 'updateRenderWindow' });
}

export async function createTerritoryCenterLine(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
) {
  if (!context.current || !pool || analysisType !== 'myocardial') return;
  const { overlayRenderer, actorIds, viewportId } = context.current;

  const data = pool?.territory?.data as territoryInfo;
  const mappingData = data?.centerline ?? [];
  const fcx = pool?.fcx.data as Float32Array;
  const fcy = pool?.fcy.data as Float32Array;
  const fcz = pool?.fcz.data as Float32Array;
  const newType = pool?.newType.data as Int32Array;
  let actor = actorIds[nodeNames.singleCenterLine] as vtkActor;
  if (!actor) {
    actor = vtkActor.newInstance();
    actor.getProperty().setColor(0, 1, 0);
    actor.getProperty().setInterpolationToPhong();
    actor.setPickable(true);
    actor.getProperty().setLighting(false);
    overlayRenderer?.addActor(actor);
  }
  const quality = configStore.get().generalSetting.renderingQuality !== 'high';
  const volume = getVolumeFromSeriesId(seriesId, quality);
  const { origin, spacing } = volume!;
  const groups: number[][] = [];
  let currentType: number | null = null;
  let current: number[] = [];

  for (let i = 0; i < mappingData.length; i++) {
    const pointId = mappingData[i];
    if (!Number.isFinite(pointId)) continue;
    const typeId = newType[pointId];
    if (currentType === null || typeId !== currentType) {
      if (current.length >= 2) groups.push(current);
      currentType = typeId;
      current = [pointId];
    } else {
      current.push(pointId);
    }
  }
  if (current.length >= 2) groups.push(current);

  const flat: number[] = [];
  const cells: number[] = [];
  let pointIndex = 0;
  groups.forEach((points) => {
    const startIndex = pointIndex;
    points.forEach((pointId) => {
      const world = getIndexToWorld(
        [fcx[pointId], fcy[pointId], fcz[pointId]],
        spacing,
        origin,
        quality,
      );
      flat.push(world[0], world[1], world[2]);
      pointIndex += 1;
    });
    cells.push(points.length);
    for (let i = 0; i < points.length; i++) {
      cells.push(startIndex + i);
    }
  });

  updateLinePolyDataOnActor(actor, flat, cells);

  context.current.actorIds[nodeNames.singleCenterLine] = actor;
}

export async function createFFRCenterLine(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  id: number,
) {
  if (!context.current || !pool || analysisType !== 'FFR') return;
  const { overlayRenderer, actorIds, viewportId } = context.current;

  const centerline = pool.FFR_centerline.data as vtkPolyData;
  const r0x1l2s = centerline.getCellData().getArrayByName('R0X1L2')?.getData();
  if(!centerline || !r0x1l2s) return;
  //centerline.buildLinks();
  const isRCA = (r0x1l2s[id] === FFR_ARTERY_TYPE.RCA);
  const points = centerline.getPoints();

  const cell = centerline.getCellPoints(id) as
    | { cellPointIds: Uint32Array | Uint16Array }
    | undefined;

  if (!cell || !cell.cellPointIds) return;
  const ptIds = cell.cellPointIds;

  const coords: number[][] = [];
  for (let i = 0; i < ptIds.length; i++) {
    const id = ptIds[i];

    const pt = points.getPoint(id);
    coords.push([pt[0], pt[1], pt[2]]);
  }

  const flat = [];
  for (let i = 0; i < coords.length; i++) {
    flat.push(coords[i][0], coords[i][1], coords[i][2]);
  }
  const count = flat.length / 3;
  const cells: number[] = [];
  cells.push(count);
  for (let i = 0; i < count; i++) {
    cells.push(i);
  }

  let actor = actorIds[nodeNames.singleCenterLine] as vtkActor;
  if (!actor) {
    actor = vtkActor.newInstance();
    actor.getProperty().setColor(0, 1, 0);
    actor.getProperty().setInterpolationToPhong();
    actor.setPickable(true);
    actor.getProperty().setLighting(false);

    actor.setMapper(vtkMapper.newInstance());
    overlayRenderer?.addActor(actor);
  }
  actor.set({ isRCA });
  actor.setVisibility(true);
  updateLinePolyDataOnActor(actor, flat, cells);
  context.current.actorIds[nodeNames.singleCenterLine] = actor;

  requestAnimationFrame(() =>
    scheduleRender(viewportId!, { type: 'updateRenderWindow' }),
  );
}

export function getFFRCenterId(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  pickId: string,
  pointId: number,
): number | undefined {
  if (analysisType !== 'FFR') return;

  const actor = context.current?.actorIds?.[pickId];
  if (!actor) return;

  const polyData = actor.getMapper()?.getInputData() as vtkPolyData | undefined;
  if (!polyData) return;

  const pointData = polyData.getPointData();
  if (!pointData) return;

  const centerlineArray =
    pointData.getArrayByName('CenterlineIds') ??
    pointData.getArrayByName('CenterlineId');

  if (!centerlineArray) return;

  const ids = centerlineArray.getData() as ArrayLike<number>;
  const id = Number(ids?.[pointId]);

  return Number.isFinite(id) ? id : undefined;
}

export function getFFRCenterPoint(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  centerlineIndex: number,
  id: number,
) {
  if (!context.current || !pool || analysisType !== 'FFR') return;
  const centerline = pool.FFR_centerline.data as vtkPolyData;

  if (!centerline) return;
  //centerline.buildLinks();

  const points = centerline.getPoints();

  const cell = centerline.getCellPoints(centerlineIndex) as
    | { cellPointIds: Uint32Array | Uint16Array }
    | undefined;

  if (!cell || !cell.cellPointIds) return;

  const cellIds = cell.cellPointIds;
  const cellId = cellIds[id];

  const world = points.getPoint(cellId) as [number, number, number];
  if (world && id !== -1) {
    const focusWorld: [number, number, number] = [
      -world[0],
      world[1],
      world[2],
    ];
    const ctViewIds = getViewportIds();

    requestAnimationFrame(() => {
      ctViewIds.forEach((viewportId) => {
        scheduleRender(viewportId, {
          type: 'focus',
          world: focusWorld,
          request: true,
          mode: 'mouse',
        });
      });
    });
  }
  return world;
}

export function updateLinePolyDataOnActor(
  actor: vtkActor,
  flat: number[],
  cells: number[],
) {
  let mapper = actor.getMapper() as vtkMapper | null;
  if (!mapper) mapper = vtkMapper.newInstance();

  let polyData = mapper.getInputData?.() as vtkPolyData | null;
  if (!polyData) {
    polyData = vtkPolyData.newInstance();
    mapper.setInputData(polyData);
    actor.setMapper(mapper);
  }

  // Points
  let points = polyData.getPoints();
  if (!points) {
    points = vtkPoints.newInstance();
    polyData.setPoints(points);
  }
  const pointData = points.getData?.() as Float32Array | undefined;
  if (!pointData || pointData.length !== flat.length) {
    points.setData(Float32Array.from(flat), 3);
  } else {
    pointData.set(flat);
    points.modified();
  }

  // Lines
  let lines = polyData.getLines();
  if (!lines) {
    lines = vtkCellArray.newInstance();
    polyData.setLines(lines);
  }
  const lineData = lines.getData?.() as Uint32Array | undefined;
  if (!lineData || lineData.length !== cells.length) {
    lines.setData(new Uint32Array(cells));
  } else {
    lineData.set(cells);
    lines.modified();
  }

  polyData.modified();
  mapper.modified?.();
  actor.modified();
}

export function findClosestScalarId(
  context: React.MutableRefObject<SurfaceContext | null>,
  key:string,
  worldPos:Vector3,
) {
  if(!context.current) return;
  const {actorIds} = context.current;
  const actor = actorIds[key];
  if(!actor) return;
  const mapper = actor.getMapper() as vtkMapper;
  const polyData = mapper.getInputData();       // vtkPolyData
  //const polyData = pool[`segmentationResult_laWall` as CPRBufferKeys].data as vtkPolyData;
  const points = polyData.getPoints();
  if (!points) return null;
  const coords = points.getData();
  const nbPoints = coords.length / 3;

  let closestId = -1;
  let minDist2 = Number.POSITIVE_INFINITY;

  let findPoint = worldPos;
  const [wx, wy, wz] = worldPos;

  for (let i = 0; i < nbPoints; i++) {
    const px = coords[i * 3 + 0];
    const py = coords[i * 3 + 1];
    const pz = coords[i * 3 + 2];

    const dx = px - wx;
    const dy = py - wy;
    const dz = pz - wz;

    const dist2 = dx * dx + dy * dy + dz * dz;
    if (dist2 < minDist2) {
      minDist2 = dist2;
      closestId = i;
      findPoint = [px,py,pz]
    }
  }
  if (closestId < 0) return;
  return {
    pointId: closestId,
    minDist: minDist2,
  };
}


export function getWallthicknessValue(
  context: React.MutableRefObject<SurfaceContext | null>,
  seriesId: string,
  pool:BufferPoolItem,
  cellId?:number
) {
  if (!context.current || !pool) return;
  const { focus, actorIds } = context.current;
  if (!focus) return;
  let value = 0;
  const mappingData = pool?.LAWT.data as Record<number, number>;
  if(mappingData && cellId){
    value = mappingData[cellId] * 0.05;
    context.current.valueLAWT = value;
  }else{
    const volume = getLawtVolumeFromSeriesId(seriesId);
    if(!volume) return 0;
    const { imageData, dimensions, voxelManager } = volume;
    if (!imageData || !voxelManager) return 0;
    const replMnWallthickness = voxelManager!
        .getCompleteScalarDataArray!() as Uint8Array;
    const [nx, ny, nz] = dimensions;
    const [x, y, z] = imageData.worldToIndexVec3(focus).map(Math.round);
    if (x < 0 || x >= nx || y < 0 || y >= ny || z < 0 || z >= nz) return 0;
    const offset = z * ny * nx + y * nx + x;
    value = replMnWallthickness[offset] * 0.05;
    console.log('기존꺼',replMnWallthickness[offset] * 0.05)
  }

  return value > 5.5 || isNaN(value) ? 0 : value;
}


function getScalarData(
  polyData:vtkPolyData,
  pointCount:number,
){
  let interpolatedScalarColors:Int8Array;
  let scalars = polyData.getPointData().getScalars();
  if (!scalars) {
    interpolatedScalarColors = new Int8Array(pointCount);
    const scalarArray = vtkDataArray.newInstance({
      name: 'LabelScalar',
      numberOfComponents: 1,
      values: interpolatedScalarColors,
      dataType: 'Int8Array', // 또는 Uint8Array 등
    });
    polyData.getPointData().setScalars(scalarArray);
  } else {
    interpolatedScalarColors = scalars.getData() as Int8Array;
    if (interpolatedScalarColors.length !== pointCount) {
      interpolatedScalarColors = new Int8Array(pointCount);
      scalars.setData(interpolatedScalarColors);
    }
  }
  return interpolatedScalarColors;
}

export async function AorticNodeRenderer(
  context: React.MutableRefObject<SurfaceContext | null>,
  world: Vector3,
  normal: Vector3,
) {
  if (!context.current) return;
  const { renderer, actorIds, viewportId } = context.current as SurfaceContext;
  const color = hexToNormalizedRGB(configStore.get().colors.avcNode);
  const aorta = actorIds['aorta'];
  const radius = aorta
    ? Math.round(Math.abs(aorta.getBounds()[1] - aorta.getBounds()[0]) * 0.4)
    : 20;

  const actorKey = 'aortic_node';
  const prevActor  = actorIds[actorKey] as vtkActor;
  const actor = createCircleSource(prevActor, world, normal!, color, radius);
  if (!prevActor){
    renderer.addActor(actor);
  }
  (actorIds as Actors)[actorKey] = actor;
  requestAnimationFrame(() =>
    scheduleRender(viewportId!, { type: 'updateRenderWindow' }),
  );
}

export function getIndexToWorld(
  index: [number, number, number],
  spacing: [number, number, number],
  origin: [number, number, number],
  half: boolean = false,
) {
  const coord: [number, number, number] = [
    -index[0],
    -index[1],
    index[2],
  ];

  const scale = half ? 0.5:1;

  const world = coord.map(
    (v: number, i: number) => v * scale * spacing[i] + origin[i]
  ) as [number, number, number];

  const finalWorld: [number, number, number] = [
    -world[0],
    world[1],
    world[2],
  ];
  return finalWorld;
}



export function cutterFFRActor(
  context: React.MutableRefObject<SurfaceContext | null>,
  pool:BufferPoolItem,
  seriesId: string,
  cutter: boolean,
  name: string,
) {
  if (!context.current|| !pool) return;
  const { actorIds, rendererId } = context.current;
  console.time('왱2');

  const names = getSegmentationAnalysisType('segmentation');
  let plane: vtkPlane | undefined;

  if (cutter) {
    const paramData = pool?.parameter.data as CalciumAnalysisResult;
    if (!paramData) return;

    const p1 = paramData.SavedInfo?.Coronary?.initialPoints1;
    const p2 = paramData.SavedInfo?.Coronary?.initialPoints2;
    if (!p1 || !p2) return;
    const cond = p1[2] > p2[2];
    const index = cond
      ? ([...p1] as [number, number, number])
      : ([...p2] as [number, number, number]);

    let safeIndex = [...index] as [number, number, number];

    const quality =
      configStore.get().generalSetting.renderingQuality !== 'high';
    const labelVolume = getVolumeFromSeriesId(seriesId, quality);
    if (!labelVolume) return;
    const { spacing, origin, dimensions } = labelVolume;
    const scale = quality ? 0.5:1;
    safeIndex[2] += 10 / (scale * spacing[2]);
    const zSize = quality ? dimensions[2] * 2 : dimensions[2];
    safeIndex[2] = zSize - safeIndex[2] - 1;
    const zOffset = quality ? dimensions[2] : dimensions[2] / 2;
    safeIndex[2] += zOffset;
    const focalPoint = getIndexToWorld(safeIndex, spacing, origin, quality);
    plane = vtkPlane.newInstance();
    plane.setOrigin(focalPoint);
    plane.setNormal(0, 0, 1);
  }
  const actor = actorIds[name] as vtkActor;
  const mapper = actor?.getMapper() as vtkMapper;
  if (!actor || !mapper) return;

  if (cutter && plane) {
    mapper.addClippingPlane(plane);
  } else {
    mapper.removeAllClippingPlanes();
  }
  mapper.modified();
  actor.modified();
  console.timeEnd('왱2');
  requestAnimationFrame(() =>
    scheduleRender(rendererId!, { type: 'updateRenderWindow' }),
  );
}

export function heartCutter(
  context: React.MutableRefObject<SurfaceContext | null>,
  bufferPool: BufferPool,
  cutter: boolean,
  seriesId: string,
) {
  if (!context.current) return;
  const { actorIds, rendererId } = context.current;
  console.time('왱2');

  const names = getSegmentationAnalysisType('segmentation');
  let plane: vtkPlane | undefined;

  if (cutter) {
    const buffers = bufferPool[seriesId];
    if (!buffers) return;
    const paramData = buffers?.parameter.data as CalciumAnalysisResult;
    if (!paramData) return;

    const p1 = paramData.SavedInfo?.Coronary?.initialPoints1;
    const p2 = paramData.SavedInfo?.Coronary?.initialPoints2;
    if (!p1 || !p2) return;
    const cond = p1[0] < p2[0] && p1[1] > p2[1];
    const index = cond
      ? ([...p1] as [number, number, number])
      : ([...p2] as [number, number, number]);

    let safeIndex = ([...index] as [number, number, number]);

    const quality = configStore.get().generalSetting.renderingQuality  !== 'high';
    const labelVolume = getVolumeFromSeriesId(
      seriesId,
      quality,
    );

    if(!labelVolume) return;

    const { spacing, origin, dimensions } = labelVolume;
    const zSize = quality ? dimensions[2] * 2 : dimensions[2];
    safeIndex[2] = zSize - safeIndex[2] - 1;
    const focalPoint = getIndexToWorld(
      safeIndex,
      spacing,
      origin,
      quality
    );
    plane = vtkPlane.newInstance();
    plane.setOrigin(focalPoint);
    plane.setNormal(0, 0, 1);
  }

  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    if (name === 'aorta' || name === 'coronary') continue;
    const actor = actorIds[name] as vtkActor;
    const mapper = actor?.getMapper() as vtkMapper;
    if (!actor || !mapper) continue;

    if (cutter && plane) {
      mapper.addClippingPlane(plane);
    } else {
      mapper.removeAllClippingPlanes();
    }
    mapper.modified();
    actor.modified();
  }
  console.timeEnd('왱2');
  requestAnimationFrame(() =>
    scheduleRender(rendererId!, { type: 'updateRenderWindow' }),
  );
}

export function convertFFRToWorld(
  seriesId: string,
  pickedPoint: [number, number, number],
): [number, number, number] | undefined {
  const { renderingQuality } = configStore.get().generalSetting;
  const isLowQuality = renderingQuality !== 'high';
  const volume = getVolumeFromSeriesId(seriesId, isLowQuality);
  if (!volume) return;
  const { origin, spacing, dimensions } = volume;
  const index = getWorldToIndex(
    pickedPoint,
    spacing,
    origin,
    isLowQuality,
  );

  const zOffset = isLowQuality
    ? dimensions[2]
    : dimensions[2] / 2;

  index[2] -= zOffset;
  const world = getIndexToWorld(
    index,
    spacing,
    origin,
    isLowQuality,
  );
  world[0] = -world[0];
  console.log({index, world, pickedPoint})
  return world;
}


export function getVolumeWorldToIndex(
  seriesId: string,
  pickedPoint: [number, number, number],
): [number, number, number] | undefined {
  if (!pickedPoint) return;

  const { renderingQuality } = configStore.get().generalSetting;
  const isLowQuality = renderingQuality !== 'high';

  const volume = getVolumeFromSeriesId(seriesId, isLowQuality);
  if (!volume) return;

  const { origin, spacing } = volume;

  return getWorldToIndex(
    pickedPoint,
    spacing,
    origin,
    isLowQuality
  );
}


export function getWorldToIndex(
  world: [number, number, number],
  spacing: [number, number, number],
  origin: [number, number, number],
  half: boolean = false,
) {
  const scale = half ? 0.5:1;
  const coord = [
    (world[0] - origin[0]) / (spacing[0] * scale),
    (world[1] - origin[1]) / (spacing[1] * scale),
    (world[2] - origin[2]) / (spacing[2] * scale),
  ] as [number, number, number];
 const index: [number, number, number] = [
    -coord[0],
    -coord[1],
    coord[2],
  ];
  return index;
}


export function getBranchType(
  seriesId: string,
  pool: BufferPoolItem,
  half?:boolean,
) {
  const renderingQuality = configStore.get().generalSetting.renderingQuality;
  const low = half?? renderingQuality !== 'high'
  const labelVolume = getVolumeFromSeriesId(
    seriesId,
    half?? renderingQuality !== 'high',
  );
  if (!labelVolume) return;
  const imageData = labelVolume.imageData;
  if (!imageData) return;

  const fcx = pool['fcx'].data as Int32Array;
  const fcy = pool['fcy'].data as Int32Array;
  const fcz = pool['fcz'].data as Int32Array;
  const newTypeIds = pool['newType'].data as Int32Array;
  const paramData = pool.parameter.data as CalciumAnalysisResult;
  const newTypes = paramData.SavedInfo.Coronary.newType;

  const { origin, spacing } = labelVolume;
  const scale = low ? 0.5:1;

  const dedupedMap = new Map<
    string,
    { name: string; world: [number, number, number] }
  >();
  const LARGE_BRANCHES = new Set(['LAD', 'RCA', 'LM', 'LCX']);
  const groupedByType: Record<string, number[][]> = {};

  // Step 1: 그룹핑 by type (연속 segment 나누기)
  for (let id = 0; id < newTypeIds.length; id++) {
    const typeId = newTypeIds[id];
    const name = newTypes[typeId][0];

    if (name.toLowerCase() === 'uncertain') continue;
    if (!groupedByType[name]) groupedByType[name] = [[]];

    const groups = groupedByType[name];
    const currentGroup = groups[groups.length - 1];

    if (
      currentGroup.length === 0 ||
      id === currentGroup[currentGroup.length - 1] + 1
    ) {
      currentGroup.push(id); // 연속
    } else {
      groups.push([id]); // 단절
    }
  }

  // Step 2: segment마다 중심 또는 말단 좌표 추출 후 dedupedMap에 저장
  for (const name in groupedByType) {
    const segments = groupedByType[name];
    for (const segment of segments) {
      if (segment.length <= 2) continue;

      const selectedIndex = LARGE_BRANCHES.has(name)
        ? Math.ceil(segment.length / 2)
        : segment.length - 1;

      const id = segment[selectedIndex];
      const world = getIndexToWorld(
        [
          fcx[id],
          fcy[id],
          fcz[id]
        ],
        spacing,
        origin,
        low
      );
      const point = {
        name,
        world,
      };
      if (LARGE_BRANCHES.has(name)) {
        dedupedMap.set(name, point); // 중복 제거 대상은 덮어씀
      } else {
        dedupedMap.set(`${name}_${Math.random().toString(36).slice(2)}`, point); // 고유 키로 저장
      }
    }
  }
  return Array.from(dedupedMap.values());
}

export function getSelectedBranchIdAndNodeIndex(
  seriesId: string,
  pool: BufferPoolItem,
  pickedPoint: [number, number, number],
) {
  if (!pickedPoint) return [-1, -1];
  const { renderingQuality } = configStore.get().generalSetting;
  const labelVolume = getVolumeFromSeriesId(
    seriesId,
    renderingQuality !== 'high',
  );
  if (!labelVolume) return [-1, -1];
  const startNode = pool['startNode_list'].data as Int32Array;
  const info = getSelectedBranchId(seriesId,pool,pickedPoint);
  const id = info[0];
  if (id < 0) return [-1, -1];
  const Index_total = info[1];
  const branch_start_node = startNode[id];
  const index = Index_total - branch_start_node;
  return [id, index];
}

export function getSelectedBranchId(
  seriesId: string,
  pool: BufferPoolItem,
  pickedPoint: [number, number, number],
) {
  if (!pickedPoint) return [-1, -1];
  const { renderingQuality } = configStore.get().generalSetting;
  const labelVolume = getVolumeFromSeriesId(
    seriesId,
    renderingQuality !== 'high',
  );
  if (!labelVolume) return [-1, -1];
  const { imageData, origin, spacing } = labelVolume;

  const fcx = pool['fcx'].data as Int32Array;
  const fcy = pool['fcy'].data as Int32Array;
  const fcz = pool['fcz'].data as Int32Array;
  const branchid = pool['branchid'].data as Int32Array;
  if (!fcx || !fcy || !fcz || !branchid) return [-1, -1];
  const result: Vector3[] = [];
  const branchIds: number[] = [...branchid];
  for (let i = 0; i < fcx.length; i++) {
    result.push([fcx[i], fcy[i], fcz[i]]);
  }

  const position = getWorldToIndex(
    pickedPoint,
    spacing,
    origin,
    renderingQuality !== 'high'
  );
  const info = findNearBranch(position, result, branchIds);
  return info;
}


function updateBackgroundColorTF(
  key: string,
  colorTF: vtkColorTransferFunction,
  color: Vector3,
) {
  if (colorTF) {
    if (key === 'coronary') {
      const range = 0.5;
      colorTF.addRGBPoint(
        0,
        color[0] * range,
        color[1] * range,
        color[2] * range,
      );
      colorTF.addRGBPoint(100, ...color);
    }
  }
}


function init3DCameraForAnalysis(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
  pool?: BufferPoolItem,
) {
  if (!context.current) return;
  const config = configStore.get();
  if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi') {
    const lowQ = configStore.get().generalSetting.renderingQuality !== 'high';
    const volume = getVolumeFromSeriesId(seriesId, lowQ);
    const taviCenterline =
      analysisType === 'tavi' && volume?.imageData
        ? getTaviCenterlinePolyData(
            pool as BufferPoolItem,
            volume.imageData as vtkImageData,
          )
        : null;
    const aorticBranchId =
      analysisType === 'tavi'
        ? (config.tavi_aorta_id ?? config.aorta_id)
        : config.aorta_id;
    const aorticCurrentIndex =
      analysisType === 'tavi'
        ? (config.tavi_current_index ?? config.aorta_current_index)
        : config.aorta_current_index;
    const centerline =
      taviCenterline ??
      (pool?.centerData.data as Record<number, vtkPolyData>)[aorticBranchId];
    if (!centerline) {
      console.log('aorta centerline 없음', config.aorta_id);
      return;
    }
    const { world, normal } = updataAorticNode(
      context,
      centerline,
      aorticCurrentIndex,
      analysisType === 'tavi'
        ? getTaviCenterTangentAtIndex(
            pool as BufferPoolItem,
            aorticCurrentIndex,
            volume?.imageData as vtkImageData,
          ) ?? undefined
        : undefined,
    );
    if(analysisType === 'aorticValveCalcium')
      AorticNodeRenderer(context, world as Vector3, normal as Vector3);
    context.current.focus = world as Vector3;
    context.current.savedTranslation = [-world[0], world[1], world[2]];
    context.current.normal = normal as Vector3;
  } else if (analysisType === 'myocardial') {
    const centerline = (pool?.centerData.data as Record<number, vtkPolyData>)[
      config.territory_branchid
    ];
    if (!centerline) {
      console.log('branch centerline 없음', config.territory_branchid);
      return;
    }
    const position = centerline
      .getPoints()
      .getPoint(config.territory_input_idx) as [number, number, number];
    context.current.focus = position;
  }

  if (analysisType === 'wallthickness') {
    const camera: vtkCamera = context.current.renderer.getActiveCamera();
    const focalPoint = camera.getFocalPoint();
    const distance = camera.getDistance(); // 초점과의 거리
    camera.setPosition(focalPoint[0], focalPoint[1] - distance, focalPoint[2]);
    camera.setViewUp(0, 0, -1);
  } else {
    const camera: vtkCamera = context.current.renderer.getActiveCamera();
    const focalPoint = camera.getFocalPoint();
    const distance = camera.getDistance();
    camera.setPosition(focalPoint[0], focalPoint[1] + distance, focalPoint[2]);
    camera.setViewUp(0, 0, -1);
  }
  context.current.axes = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, 1],
  };
  clearAxesLinesAt({ context });
  updateCameraForAnalysis(context, analysisType, seriesId, pool);
}

export async function captureSixDirections(
  context: React.RefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
):Promise<string[] | undefined> {
  if (!context.current) return;

  const { renderer, renderWindow, orientationWidget } = context.current;

  const camera: vtkCamera = renderer.getActiveCamera();
  const oriPosition = camera.getPosition();
  const oriViewUp = camera.getViewUp();
  const oriFocalPosition = camera.getFocalPoint();
  //const world = context.current.focus as [number, number, number];

  if (analysisType === 'myocardial') {
   const center = context.current.savedTranslation as [
     number,
     number,
     number,
   ];
   context.current.focus = [-center[0], center[1], center[2]];
  }

  orientationWidget?.setEnabled(false);
  const directions = ['anterior', 'posterior', 'left', 'right', 'head', 'feet','anterior'];
  const results = await Promise.all(
    directions.slice(0, 7).map(async (key) => {
      const cam: vtkCamera = renderer.getActiveCamera();
      const focalPoint = cam.getFocalPoint();
      const distance = cam.getDistance();

      const set = cam.setPosition.bind(cam);

      // 방향별 카메라 이동
      switch (key) {
        case 'posterior':
          set(focalPoint[0], focalPoint[1] - distance, focalPoint[2]);
          cam.setViewUp(0, 0, -1);
          break;
        case 'left':
          set(focalPoint[0], focalPoint[1] + distance, focalPoint[2]);
          cam.setViewUp(0, 0, -1);
          break;
        case 'right':
          set(focalPoint[0] + distance, focalPoint[1], focalPoint[2]);
          cam.setViewUp(0, 0, -1);
          break;
        case 'head':
          set(focalPoint[0] - distance, focalPoint[1], focalPoint[2]);
          cam.setViewUp(0, 0, -1);
          break;
        case 'feet':
          set(focalPoint[0], focalPoint[1], focalPoint[2] + distance);
          cam.setViewUp(0, -1, 0);
          break;
        case 'anterior':
          set(focalPoint[0], focalPoint[1], focalPoint[2] - distance);
          cam.setViewUp(0, 1, 0);
          break;
      }

      updateResetCamera(context, analysisType);
      renderWindow.render();
      const img = await captureCanvas(renderWindow.captureImages());
      return img;
    })
  );

  //캡처 전 원래 위치로
  updateResetCamera(context, analysisType);
  camera.setPosition(...oriPosition);
  camera.setFocalPoint(...oriFocalPosition);
  camera.setViewUp(oriViewUp);
  renderer.resetCameraClippingRange();
  orientationWidget?.setEnabled(true);
  return results.slice(0, 6);
}

function setLighting(prop: vtkProperty, lightingOption: LightingOption) {
  if (lightingOption.enabled) {
    prop.setAmbient(lightingOption.ambient);
  }else{
    prop.setAmbient(0.0);
  }
}

export function updateAmbientLight(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
  lightingOption: LightingOption,
) {
  if (!context.current) return;
  const { viewportId, renderer, actorIds } = context.current;
  if (viewportId === 'Surface3D') {
    const keys = getSegmentationAnalysisType(analysisType);
    Object.values(keys).forEach((keys) => {
      const actor = actorIds[keys] as vtkActor;
      if (!actor) return;
      setLighting(actor.getProperty(),lightingOption);
    });
  }
}
